import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';

interface KeyboardShortcutsContextValue {
  openShortcutsModal: () => void;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | null>(
  null,
);

interface KeyboardShortcutsProviderProps {
  children: React.ReactNode;
}

export function KeyboardShortcutsProvider({ children }: KeyboardShortcutsProviderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const openShortcutsModal = useCallback(() => setIsOpen(true), []);
  const closeShortcutsModal = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '?') return;
      // Ignore while typing in inputs / textareas / contenteditable.
      const target = event.target as HTMLElement | null;
      if (
        // eslint-disable-next-line lingui/no-unlocalized-strings -- CSS selector, not user text
        target?.matches?.('input, textarea, select, [contenteditable="true"]') ||
        target?.closest?.('[contenteditable="true"]')
      ) {
        return;
      }
      event.preventDefault();
      setIsOpen((prev) => !prev);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <KeyboardShortcutsContext.Provider value={{ openShortcutsModal }}>
      {children}
      <KeyboardShortcutsModal isOpen={isOpen} onClose={closeShortcutsModal} />
    </KeyboardShortcutsContext.Provider>
  );
}

export function useKeyboardShortcutsModal(): KeyboardShortcutsContextValue {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    return { openShortcutsModal: () => {} };
  }
  return context;
}
